import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Bell, BellOff, Menu, MessagesSquare, Phone, Pin, Radio, Search, Upload, UserRound, Users, Video, Volume2 } from 'lucide-react';
import { Permission } from '@spawnwire/shared';
import { Avatar, clsx, IconButton, SystemTag, Tooltip } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { useIsMobile, useIsWide } from '../../lib/hooks';
import { setCurrentChannel } from '../../lib/nav';
import { can, useChannelPermissions } from '../../lib/perms';
import { joinVoice, toggleCamera, useVoice } from '../../lib/voice';
import { dmPartner, isMuted, privateChannelName, useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { MemberList } from '../members/MemberList';
import { ProfilePanel } from '../members/Profile';
import { ForumView } from '../forum/ForumView';
import { StageView } from '../voice/StageView';
import { CallPanel, VoiceView } from '../voice/VoiceView';
import { channelIcon, isPrivateChannel } from '../shell/SpaceSidebar';
import { Composer, type ComposerHandle } from './Composer';
import { MessageList } from './MessageList';
import { PinsPanel } from './PinsPanel';
import { SearchPanel } from './SearchPanel';
import { ThreadPanel } from './ThreadPanel';
import { TypingIndicator } from './TypingIndicator';

function Header({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const name = useStore((s) => (channel ? privateChannelName(s, channel) : ''));
  const partner = useStore((s) => (channel?.type === 'dm' ? dmPartner(s, channel) : null));
  const presence = useStore((s) => (partner ? (s.presences[partner.id] ?? 'offline') : null));
  const everyoneId = useStore((s) => (channel?.spaceId ? s.spaces[channel.spaceId]?.roles.find((r) => r.isDefault)?.id : undefined));
  const parent = useStore((s) => (channel?.parentId ? s.channels[channel.parentId] : undefined));
  const muted = useStore((s) => isMuted(s, channelId));
  const rightPanel = useUi((s) => s.rightPanel);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  if (!channel) return null;
  const isPrivate = !channel.spaceId;
  const Icon = channelIcon(channel, isPrivateChannel(channel, everyoneId, parent));

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3 shadow-sm">
      {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
      {partner ? <Avatar user={partner} size={24} status={presence} ringClass="ring-main" /> : channel.type === 'group_dm' ? <Users className="size-5 text-faint" aria-hidden /> : <Icon className="size-5 shrink-0 text-faint" aria-hidden />}
      <h1 className="truncate font-semibold text-fg">
        {isPrivate ? name : channel.name}
        {partner?.system && <SystemTag />}
      </h1>
      {channel.topic && !isMobile && (
        <>
          <span className="h-5 w-px bg-line" aria-hidden />
          <Tooltip label={channel.topic}>
            <p className="min-w-0 truncate text-sm text-muted">{channel.topic}</p>
          </Tooltip>
        </>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {isPrivate && !partner?.system && (
          <>
            <IconButton icon={Phone} label="Start voice call" onClick={() => void joinVoice(channelId)} />
            <IconButton
              icon={Video}
              label="Start video call"
              onClick={() =>
                void joinVoice(channelId).then(() => {
                  if (useVoice.getState().channelId === channelId && !useVoice.getState().video) void toggleCamera();
                })
              }
            />
          </>
        )}
        {(channel.type === 'text' || channel.type === 'announcement') && <IconButton icon={MessagesSquare} label="Threads" active={rightPanel === 'thread'} onClick={() => ui.toggleRightPanel('thread')} />}
        <IconButton
          icon={muted ? BellOff : Bell}
          label={muted ? 'Unmute channel' : 'Mute channel'}
          active={muted}
          onClick={() => void attempt(() => http.put(`/users/@me/notification-settings/${channelId}`, { mutedUntil: muted ? null : new Date(Date.now() + 365 * 86400_000).toISOString() }))}
        />
        <IconButton icon={Pin} label="Pinned messages" active={rightPanel === 'pins'} onClick={() => ui.toggleRightPanel('pins')} />
        {channel.type === 'dm' ? (
          // In a one-to-one conversation the side panel shows the other person's profile.
          <IconButton icon={UserRound} label={rightPanel === 'members' ? 'Hide user profile' : 'Show user profile'} active={rightPanel === 'members'} onClick={() => ui.toggleRightPanel('members')} />
        ) : (
          <IconButton icon={Users} label={channel.type === 'group_dm' ? 'Members' : 'Member list'} active={rightPanel === 'members'} onClick={() => ui.toggleRightPanel('members')} />
        )}
        {!isMobile ? (
          <form
            role="search"
            className="relative ml-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim()) ui.search(query.trim());
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              aria-label="Search messages"
              className="h-7 w-36 rounded-md bg-rail pl-2 pr-7 text-sm text-fg placeholder:text-faint outline-none transition-[width] focus:w-56"
            />
            <Search className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
          </form>
        ) : (
          <IconButton icon={Search} label="Search" onClick={() => ui.search('')} />
        )}
      </div>
    </header>
  );
}

export function ChannelView({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const rightPanel = useUi((s) => s.rightPanel);
  const mobilePane = useUi((s) => s.mobilePane);
  const isMobile = useIsMobile();
  const isWide = useIsWide();
  const perms = useChannelPermissions(channelId);
  const composer = useRef<ComposerHandle>(null);
  const [dragging, setDragging] = useState(false);
  const [params, setParams] = useSearchParams();
  const voiceChannel = useVoice((v) => v.channelId);
  const callActive = useStore((s) => Object.keys(s.voiceStates[channelId] ?? {}).length > 0);

  useEffect(() => {
    setCurrentChannel(channelId);
    return () => setCurrentChannel(null);
  }, [channelId]);

  // Deep links: ?m=<messageId> jumps to that message.
  useEffect(() => {
    const m = params.get('m');
    if (m) {
      ui.jumpTo(channelId, m);
      params.delete('m');
      setParams(params, { replace: true });
    }
  }, [params, setParams, channelId]);

  if (!channel) return null;

  if (channel.type === 'voice' || channel.type === 'stage') {
    const stage = channel.type === 'stage';
    const CallIcon = stage ? Radio : Volume2;
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
          {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
          <CallIcon className="size-5 text-faint" aria-hidden />
          <h1 className="truncate font-semibold text-fg">{channel.name}</h1>
        </header>
        {stage ? <StageView channelId={channelId} /> : <VoiceView channelId={channelId} />}
      </div>
    );
  }

  if (channel.type === 'forum' || channel.type === 'media') return <ForumView channelId={channelId} />;

  const canUpload = can(perms, Permission.ATTACH_FILES) && can(perms, Permission.SEND_MESSAGES);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) composer.current?.addFiles(files);
  };

  // One-to-one conversations show the other person's profile where the member list would be.
  const dmPartnerId = channel.type === 'dm' ? dmPartner(useStore.getState(), channel)?.id : undefined;
  const panel = rightPanel === 'members' && channel.type === 'dm' && !dmPartnerId ? null : rightPanel;
  const showPanel = !!panel && (!isMobile || mobilePane === 'side');
  const overlayPanel = showPanel && !isWide && !isMobile;
  const hideMain = isMobile && showPanel;

  return (
    <div className="flex min-h-0 flex-1">
      <section
        className={clsx('relative flex min-w-0 flex-1 flex-col', hideMain && 'hidden')}
        onDragEnter={(e) => {
          if (e.dataTransfer.types.includes('Files')) setDragging(true);
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) e.preventDefault();
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={onDrop}
        aria-label="Conversation"
      >
        <Header channelId={channelId} />
        {!channel.spaceId && (callActive || voiceChannel === channelId) && <CallPanel channelId={channelId} />}
        <MessageList channelId={channelId} />
        <div className="relative">
          <Composer ref={composer} channelId={channelId} />
          <div className="absolute inset-x-0 bottom-0">
            <TypingIndicator channelId={channelId} />
          </div>
        </div>
        {dragging && (
          <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-main/90" onDragLeave={() => setDragging(false)}>
            <div className="text-center">
              <Upload className="mx-auto size-10 text-accent" aria-hidden />
              <p className="mt-2 font-semibold text-fg">{canUpload ? 'Drop files to upload' : 'You cannot upload files here'}</p>
            </div>
          </div>
        )}
      </section>
      {showPanel && (
        <aside className={clsx('flex flex-col border-l border-line bg-sidebar', isMobile ? 'w-full' : panel === 'members' ? (dmPartnerId ? 'w-[300px] shrink-0' : 'w-60 shrink-0') : 'w-[300px] shrink-0', overlayPanel && 'absolute bottom-0 right-0 top-12 z-30 shadow-pop')}>
          {panel === 'members' && (dmPartnerId ? <ProfilePanel key={dmPartnerId} userId={dmPartnerId} /> : <MemberList channelId={channelId} />)}
          {panel === 'pins' && <PinsPanel channelId={channelId} />}
          {panel === 'search' && <SearchPanel channelId={channelId} />}
          {panel === 'thread' && <ThreadPanel parentId={channelId} />}
        </aside>
      )}
    </div>
  );
}
